import type { ReactElement } from "react";

import { cn } from "~/lib/utils";
import { APP_SHELL_COLUMN_LAYOUT_CLASS } from "./appShellLayout";
import {
  APP_TOP_BAR_CLASS,
  APP_TOP_BAR_LEFT_ZONE_CLASS,
  APP_TOP_BAR_LOGO_BUTTON_CLASS,
} from "./appTopBarLayout";
import { AppTopBarLogo } from "./branding/AppTopBarLogo";

/**
 * What the Sentry boundary draws once the app has thrown.
 *
 * Deliberately a static replica of `AppTopBar` rather than the component
 * itself: this renders after the router (and anything else) has already
 * thrown, so it may depend on nothing but the layout strings and the logo.
 * Keep the zone structure in sync with `AppTopBar` — the left zone takes its
 * classes from the same constant, so the drag contract documented in
 * `appTopBarLayout` cannot drift apart from the running app's. A crash is
 * precisely when a user needs to be able to move the window.
 */
export function AppErrorBoundaryFallback(props: {
  readonly onGoToNewChat: () => void;
}): ReactElement {
  return (
    <div className={cn(APP_SHELL_COLUMN_LAYOUT_CLASS, "bg-background text-foreground")}>
      <header className={APP_TOP_BAR_CLASS}>
        <div className={cn(APP_TOP_BAR_LEFT_ZONE_CLASS, "pl-1 md:pl-2.5")}>
          <button
            type="button"
            aria-label="Go to new chat"
            onClick={props.onGoToNewChat}
            className={`${APP_TOP_BAR_LOGO_BUTTON_CLASS} hover:bg-accent/70`}
          >
            <AppTopBarLogo />
          </button>
        </div>
        <div />
      </header>
      <div className="flex min-h-0 flex-1 items-center justify-center px-4">
        <div className="max-w-md rounded-xl border border-border bg-card p-6 text-center shadow-lg">
          <h1 className="text-xl font-semibold tracking-tight">Something went wrong.</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Forge hit an unexpected error and reported it.
          </p>
          <button
            className="mt-4 inline-flex rounded-md border border-border px-3 py-2 text-sm font-medium hover:bg-accent"
            onClick={() => window.location.reload()}
            type="button"
          >
            Reload app
          </button>
        </div>
      </div>
    </div>
  );
}
